import type { DatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import type { HalfEdgeToFloorPath } from "building-types/floors/floorSystemTypes";
import type { FloorPath } from "building-types/floors/floorTypes";
import type { SheathingComponentEntity } from "building-types/floors/sheathingComponentTypes";
import type { SlabComponentEntity } from "building-types/floors/slabComponentTypes";
import type { FoundationAssemblyEntity } from "building-types/foundations/foundationAssemblyEntityTypes";
import type { FoundationAssemblyOverrides } from "building-types/foundations/foundationAssemblyEntityTypes";
import type { FoundationAssembly } from "building-types/foundations/foundationAssemblyEntityTypes";
import type { FoundationComponentEntity } from "building-types/foundations/foundationComponentEntityTypes";
import { BrickLedgeType, SlabType } from "building-types/foundations/foundationEnums";
import type {
  FoundationPattern,
  GeneratedIsolatedFoundationPattern,
} from "building-types/foundations/foundationPatternTypes";
import type { FoundationPath, HalfEdgeToFoundationPath } from "building-types/foundations/foundationTypes";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { Level } from "building-types/levels/levelTypes";
import type {
  LevelHalfEdgeAssociationTable,
  PlanarGraphLevelAssociations,
} from "building-types/planarGraphs/planarGraphAssociationTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { StaticRoom } from "building-types/rooms/staticRoomTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { DoorSettings } from "building-types/settings/doorSettings";
import type { FoundationSystemSettings } from "building-types/settings/foundationSettingsTypes";
import type { Wainscoting } from "building-types/wainscoting/wainscotingTypes";
import { PathPriority } from "building-types/walls/pathPriority";
import type { Table } from "common/data/table";
import { assertIsNotNullOrUndefined, ensureDefined } from "common/utils/assertOfType";
import { determineFoundationCondition, foundationHash } from "doc/utils/foundationSystemTools";
import { pathPriorityOpposite } from "doc/utils/wallTools";
import { line2Normal } from "geom/line2";
import { planarGraphAdjacentHalfEdge } from "geom/planarGraph";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { vec2Dot } from "geom/vec2";
import { floorPathOpposite, floorPathTop } from "../floor/floorPathUtils";
import {
  foundationAssemblyTop,
  getActiveFoundationAssemblyOverride,
  getFoundationSystemBrickLedgeType,
  makeFoundationAssembly,
  makeFoundationPathAssemblyOverride,
  makeGeneratedIsolatedFoundationAssemblies,
  makeRoomEdgeIsolatedFoundationAssemblies,
} from "./foundationAssemblyTools";
import { foundationPathNew, foundationPathOpposite, getFoundationPathDatums } from "./foundationStrip";
import { floorPathCoordinates, foundationPathCoordinates } from "./pathCoordinates";
import { slabTypeAtFoundationPath } from "./slabUtils";

export interface MakeFoundationPathOptions {
  readonly level: Level;
  readonly planarGraph: PolygonPlanarGraph;
  readonly planarGraphs: Table<PolygonPlanarGraph>;
  readonly docRooms: Table<StaticRoom>;
  readonly doorSettings: DoorSettings;
  readonly datumAbstractions: Table<DatumAbstraction>;
  readonly foundationSettings: FoundationSystemSettings;
  readonly foundationComponents: Table<FoundationComponentEntity>;
  readonly foundationAssemblies: Table<FoundationAssemblyEntity>;
  readonly framingComponents: Table<FramingComponentEntity>;
  readonly slabComponents: Table<SlabComponentEntity>;
  readonly sheathingComponents: Table<SheathingComponentEntity>;
  readonly issues: BuildingGenerationIssue[];
  readonly levels: Table<Level>;
  readonly planarGraphLevelAssociations: Table<PlanarGraphLevelAssociations>;
  readonly floorPaths: Table<HalfEdgeToFloorPath>;
  readonly wainscoting: Table<Wainscoting>;
  readonly foundationPatterns: LevelHalfEdgeAssociationTable<FoundationPattern>;
  readonly generatedIsolatedFoundationPatterns: LevelHalfEdgeAssociationTable<GeneratedIsolatedFoundationPattern>;
  readonly foundationAssemblyOverrides: LevelHalfEdgeAssociationTable<FoundationAssemblyOverrides>;
  readonly horizontalAssemblies: boolean;
}

function makeWainscotingLevelHalfEdgeAssociationTable(opts: MakeFoundationPathOptions) {
  const { levels, wainscoting } = opts;
  const halfEdgeHasWainscoting: { [levelId: string]: { [halfEdgeIndex: number]: boolean } } = {};

  for (const levelId in levels) {
    halfEdgeHasWainscoting[levelId] = {};
  }

  if (wainscoting) {
    for (const wainscotId in wainscoting) {
      const wainscot = wainscoting[wainscotId];
      for (const wallId in wainscot.faceInfo) {
        for (const faceSet of wainscot.faceInfo[wallId]) {
          const halfEdge = faceSet.halfEdge;
          const oppHalfEdge = planarGraphAdjacentHalfEdge(halfEdge);

          halfEdgeHasWainscoting[wainscot.levelId][halfEdge] = true;
          halfEdgeHasWainscoting[wainscot.levelId][oppHalfEdge] = true;
        }
      }
    }
  }

  return halfEdgeHasWainscoting;
}

export function makeFoundationPaths(opts: MakeFoundationPathOptions): HalfEdgeToFoundationPath {
  const { planarGraph, level } = opts;
  const { halfEdges } = planarGraph.planarGraph;

  const halfEdgeHasWainscoting = makeWainscotingLevelHalfEdgeAssociationTable(opts);
  const halfEdgeToFoundationPath: HalfEdgeToFoundationPath = {};

  for (let i = 0, l = halfEdges.length; i < l; i += 2) {
    const halfEdgeIndex = i / 2;
    const oppHalfEdgeIndex = planarGraphAdjacentHalfEdge(halfEdgeIndex);

    if (halfEdgeToFoundationPath[halfEdgeIndex]) {
      continue;
    }

    const foundationPath = makeFoundationPath(halfEdgeIndex, level.id, opts, halfEdgeHasWainscoting);
    if (foundationPath) {
      halfEdgeToFoundationPath[halfEdgeIndex] = foundationPath;
      halfEdgeToFoundationPath[oppHalfEdgeIndex] = foundationPathOpposite(foundationPath, opts.planarGraphs);
    }
  }

  return halfEdgeToFoundationPath;
}

/**
 * @NOTE - makeFoundationPath is recursive - it relies on foundation paths above and below
 * the current halfEdgeIndex, and will build out those paths in order to generate the appropriate path.
 * Modify with caution
 */
function makeFoundationPath(
  halfEdgeIndex: number,
  levelId: string | undefined,
  opts: MakeFoundationPathOptions,
  wainscot: ReturnType<typeof makeWainscotingLevelHalfEdgeAssociationTable>,
): FoundationPath | undefined {
  const {
    levels,
    floorPaths,
    foundationAssemblyOverrides,
    datumAbstractions,
    foundationSettings,
    foundationComponents,
    foundationAssemblies,
    foundationPatterns,
    generatedIsolatedFoundationPatterns,
    framingComponents,
    slabComponents,
    sheathingComponents,
    planarGraphLevelAssociations,
    issues,
  } = opts;

  const foundationHashKey = foundationHash(
    foundationSettings.foundationSuperSystemType,
    foundationSettings.foundationSubSystemType,
  );

  assertIsNotNullOrUndefined(levelId);

  const level = levels[levelId];
  const levelTop = level.floorElevation + level.topOfWall;
  const oppHalfEdgeIndex = planarGraphAdjacentHalfEdge(halfEdgeIndex);
  const floorPath = floorPaths[levelId][halfEdgeIndex];
  const { leftRoom, rightRoom, leftRoomClass, rightRoomClass } = floorPath;
  const [startX, startY, endX, endY] = floorPathCoordinates(floorPath, opts.planarGraphs);
  const getStaticRoom = (room: Room) => {
    const lookupRoomId = room?.sourceRoomId ?? room?.id;
    return lookupRoomId ? opts.docRooms[lookupRoomId] : undefined;
  };
  const getRoomDisabledFoundationGeneration = (room: Room) => {
    return getStaticRoom(room)?.disableFoundationGeneration ?? undefined;
  };

  if (opts.horizontalAssemblies) {
    const leftStaticRoom = getStaticRoom(leftRoom);
    const leftRoomNotPresentOrDisabledFoundationGeneration = leftStaticRoom
      ? leftStaticRoom.disableFoundationGeneration ?? false
      : true;
    const rightStaticRoom = getStaticRoom(rightRoom);
    const rightRoomNotPresentOrDisabledFoundationGeneration = rightStaticRoom
      ? rightStaticRoom.disableFoundationGeneration ?? false
      : true;
    if (leftRoomNotPresentOrDisabledFoundationGeneration && rightRoomNotPresentOrDisabledFoundationGeneration) {
      return undefined;
    }
  }
  const [pnX, pnY] = line2Normal(startX, startY, endX, endY);

  // recursive call to makeFoundationPath that returns the foundationPaths
  // for the level below that are collinear with this foundation path.
  // These are oriented to match the current path
  const getOrientedFoundationPathsBelow = (): {
    foundationPaths: FoundationPath[];
    floorPaths: FloorPath[];
  } => {
    if (level.levelBelow === undefined) {
      return { foundationPaths: [], floorPaths: [] };
    }
    const foundationPaths: FoundationPath[] = [];
    const floorPathsResult: FloorPath[] = [];
    const levelBelowId = ensureDefined(level.levelBelow);
    const floorPathsBelow = floorPaths[levelBelowId];

    if (!floorPathsBelow) {
      return { foundationPaths: [], floorPaths: [] };
    }

    for (const heBelow of planarGraphLevelAssociations[levelId].halfEdgesBelow[halfEdgeIndex]) {
      const foundationPathBelow = makeFoundationPath(heBelow, levelBelowId, opts, wainscot);
      if (!foundationPathBelow) {
        continue;
      }
      const floorPathBelow = floorPathsBelow[heBelow];
      // Use getAlignedPathCoordinates to validate alignment and get coordinates
      const [bStartX, bStartY, bEndX, bEndY] = foundationPathCoordinates(foundationPathBelow, opts.planarGraphs);
      const [pbnX, pbnY] = line2Normal(bStartX, bStartY, bEndX, bEndY);

      if (vec2Dot(pnX, pnY, pbnX, pbnY) < 0) {
        foundationPaths.push(foundationPathOpposite(foundationPathBelow, opts.planarGraphs));
        floorPathsResult.push(floorPathOpposite(floorPathBelow));
      } else {
        foundationPaths.push(foundationPathBelow);
        floorPathsResult.push(floorPathBelow);
      }
    }

    return { foundationPaths, floorPaths: floorPathsResult };
  };

  const { brickLedgeType: defaultBrickLedgeType } = getFoundationSystemBrickLedgeType(
    foundationAssemblies,
    foundationComponents,
    foundationSettings,
    datumAbstractions,
  );

  // maintain existing plan sets with global NONE type on brick ledge
  const halfEdgeHasBrickLedge = wainscot[level.id][halfEdgeIndex] && defaultBrickLedgeType !== BrickLedgeType.NONE;
  const foundationCondition = determineFoundationCondition(
    leftRoomClass,
    opts.horizontalAssemblies ? getRoomDisabledFoundationGeneration(leftRoom) ?? false : false,
    rightRoomClass,
    opts.horizontalAssemblies ? getRoomDisabledFoundationGeneration(rightRoom) ?? false : false,
    halfEdgeHasBrickLedge,
    issues,
  );

  const pathDatums = getFoundationPathDatums(
    { condition: foundationCondition },
    floorPath,
    level,
    framingComponents,
    slabComponents,
    sheathingComponents,
    foundationSettings,
  );

  // Create a temporary foundation path for assembly generation
  const foundationPathPartial = foundationPathNew(
    halfEdgeIndex,
    levelId,
    foundationCondition,
    {} as FoundationAssembly, // temporary, will be replaced
    pathDatums,
    [],
    0,
    0,
  );

  const foundationAssemblyOverride = makeFoundationPathAssemblyOverride(
    floorPath,
    getActiveFoundationAssemblyOverride(foundationAssemblyOverrides[levelId], halfEdgeIndex, foundationHashKey),
    getActiveFoundationAssemblyOverride(foundationAssemblyOverrides[levelId], oppHalfEdgeIndex, foundationHashKey),
    foundationAssemblies,
    foundationComponents,
    foundationSettings,
    datumAbstractions,
  );

  const { foundationPaths: foundationPathsBelow, floorPaths: floorPathsBelow } = getOrientedFoundationPathsBelow();
  const foundationAssembly = makeFoundationAssembly({
    level,
    foundationPath: foundationPathPartial,
    floorPath,
    foundationAssemblies,
    foundationComponents,
    foundationSettings,
    datumAbstractions,
    foundationPatterns: foundationPatterns[levelId],
    foundationAssemblyOverride,
    foundationPathsBelow,
    floorPathsBelow,
  });

  const isolatedAssemblies = [
    ...makeRoomEdgeIsolatedFoundationAssemblies({
      foundationPath: foundationPathPartial,
      foundationAssemblies,
      foundationComponents,
      foundationSettings,
      datumAbstractions,
      foundationPatterns: foundationPatterns[levelId],
      foundationPathsBelow,
      planarGraphs: opts.planarGraphs,
    }),
    ...makeGeneratedIsolatedFoundationAssemblies({
      foundationPath: foundationPathPartial,
      foundationAssemblies,
      foundationComponents,
      foundationSettings,
      datumAbstractions,
      generatedIsolatedFoundationPatterns: generatedIsolatedFoundationPatterns[levelId],
      planarGraphs: opts.planarGraphs,
    }),
  ];

  const foundationTop = foundationAssemblyTop({
    foundationPath: foundationPathPartial,
    floorPath: floorPath,
    foundationAssembly,
    foundationSettings,
    foundationComponents,
    framingComponents,
    slabComponents,
    sheathingComponents,
    level,
  });

  // furring walls are adjusted in the wall path since they are linked
  if (level.floorIndex < 0 && foundationTop >= levelTop) {
    return foundationPathNew(
      halfEdgeIndex,
      levelId,
      foundationCondition,
      foundationAssembly,
      pathDatums,
      isolatedAssemblies,
      floorPath.floorOffset,
      floorPath.floorOffset,
    );
  }

  let subSystemOffset = floorPath.floorOffset;
  let superSystemOffset = floorPath.floorOffset;

  const { lateralDatumAbstraction: assemblyDatumAbstraction } = foundationAssembly;
  let assemblyDatumOffset = assemblyDatumAbstraction.resolveLateralDatum();

  let assemblyBelowOverlaps = false;
  let assemblyBelowFloorOffset: number | undefined;

  if (foundationPathsBelow.length) {
    const belowFloorOffset = Math.max(
      ...floorPathsBelow.map((floorPathBelow) => {
        return floorPathBelow.floorOffset;
      }),
    );

    const belowAssemblyOffset = Math.max(
      ...foundationPathsBelow.map((foundationPath) => {
        return foundationPath.assembly.lateralDatumAbstraction.resolveLateralDatum();
      }),
    );

    const belowTop = Math.max(
      ...foundationPathsBelow.map((foundationPath, index) => {
        return foundationAssemblyTop({
          foundationPath: foundationPath,
          floorPath: floorPathsBelow[index],
          foundationAssembly: foundationPath.assembly,
          foundationSettings: foundationSettings,
          foundationComponents: foundationComponents,
          framingComponents,
          slabComponents,
          sheathingComponents,
          level: levels[ensureDefined(level.levelBelow)],
        });
      }),
    );

    const minPriority = pathPriorityOpposite(floorPath.floorPriority);
    const foundationPathTop = floorPathTop(level, floorPath, minPriority);

    assemblyDatumOffset = belowAssemblyOffset;
    assemblyBelowFloorOffset = belowFloorOffset;
    if (foundationPathTop <= belowTop) {
      assemblyBelowOverlaps = true;
    }
  }

  const { floorPriority } = floorPath;

  if (slabTypeAtFoundationPath({ condition: foundationCondition }, foundationSettings) === SlabType.NONE) {
    if (floorPriority === PathPriority.LEFT) {
      subSystemOffset += assemblyDatumOffset;
    } else if (floorPriority === PathPriority.RIGHT) {
      subSystemOffset -= assemblyDatumOffset;
    } else {
      subSystemOffset = assemblyBelowFloorOffset ?? 0;
    }
  }

  if (assemblyBelowOverlaps) {
    superSystemOffset = subSystemOffset;
  }

  return foundationPathNew(
    halfEdgeIndex,
    levelId,
    foundationCondition,
    foundationAssembly,
    pathDatums,
    isolatedAssemblies,
    subSystemOffset,
    superSystemOffset,
  );
}
